import { useEffect, useMemo, useState } from 'react'
import { FileDown, Loader2, Search, CheckSquare, Square, MapPin, Building2 } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { toast } from '@/components/ui/Toast'
import { cn } from '@/lib/cn'
import { normalizeForMatch } from '@/lib/validators'
import { CatalogService, type CatalogLocation } from '@/services/modules/CatalogService'

type Mode = 'state' | 'city'

export function MyCompanyCatalogPage() {
  const [mode, setMode] = useState<Mode>('state')
  const [items, setItems] = useState<CatalogLocation[]>([])
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [search, setSearch] = useState('')

  // Carrega a lista (estados ou cidades) ao trocar de modo.
  useEffect(() => {
    let alive = true
    setLoading(true)
    setSelected(new Set())
    setSearch('')
    const fetcher = mode === 'state' ? CatalogService.states() : CatalogService.cities()
    fetcher
      .then((d) => { if (alive) setItems(d) })
      .catch(() => toast.error('Erro ao carregar a lista.'))
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [mode])

  const filtered = useMemo(() => {
    const q = normalizeForMatch(search)
    const base = q ? items.filter((i) => normalizeForMatch(`${i.name} ${i.uf}`).includes(q)) : items
    return [...base].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }))
  }, [items, search])

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }
  const selectAll = () => setSelected(new Set(filtered.map((i) => i.id)))
  const clearAll = () => setSelected(new Set())

  async function generate() {
    if (selected.size === 0) {
      toast.error(mode === 'state' ? 'Selecione pelo menos um estado.' : 'Selecione pelo menos uma cidade.')
      return
    }
    setGenerating(true)
    try {
      const blob = await CatalogService.generate(mode, [...selected])
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'rede-credenciada.pdf'
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
      toast.success('PDF gerado.')
    } catch {
      toast.error('Erro ao gerar o PDF.')
    } finally {
      setGenerating(false)
    }
  }

  const label = mode === 'state' ? 'estado(s)' : 'cidade(s)'

  return (
    <>
      <PageHeader
        title="Catálogo"
        subtitle="Gere o PDF da rede credenciada (credenciados aceitos)"
        crumbs={[{ label: 'Links' }, { label: 'Catálogo' }]}
      />

      {/* Modo: por estado OU por cidade (mutuamente exclusivo) */}
      <div className="bg-white rounded-md ring-1 ring-gray-200 p-4 sm:p-5 mb-4">
        <label className="block text-[12px] text-gray-600 mb-2">Agrupar e filtrar por</label>
        <div className="inline-flex rounded-md ring-1 ring-gray-200 overflow-hidden">
          <ModeButton active={mode === 'state'} onClick={() => setMode('state')} icon={<MapPin size={14} />}>Por estado</ModeButton>
          <ModeButton active={mode === 'city'} onClick={() => setMode('city')} icon={<Building2 size={14} />} divider>Por cidade</ModeButton>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Selecione um ou mais {label}. Não é possível misturar estados e cidades.
        </p>
      </div>

      {/* Seleção */}
      <div className="bg-white rounded-md ring-1 ring-gray-200 p-4 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div className="w-full sm:w-64">
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Filtrar ${label}`} iconLeft={<Search size={14} />} clearable />
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400">{selected.size} selecionado(s)</span>
            <button type="button" onClick={selectAll} className="cursor-pointer inline-flex items-center gap-1 text-xs text-brand-600 hover:underline">
              <CheckSquare size={13} /> Marcar todos
            </button>
            <button type="button" onClick={clearAll} className="cursor-pointer inline-flex items-center gap-1 text-xs text-gray-500 hover:underline">
              <Square size={13} /> Desmarcar todos
            </button>
          </div>
        </div>

        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 size={20} className="animate-spin text-gray-400" /></div>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-gray-400 py-8 text-center">
            {items.length === 0 ? 'Nenhum credenciado aceito disponível.' : 'Nada encontrado para a busca.'}
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[460px] overflow-y-auto p-1">
            {filtered.map((it) => {
              const on = selected.has(it.id)
              return (
                <label key={it.id} className={cn(
                  'cursor-pointer flex items-center gap-2 rounded-md ring-1 px-3 py-2 text-sm transition',
                  on ? 'ring-brand-300 bg-brand-50 text-brand-800' : 'ring-gray-200 text-gray-700 hover:bg-gray-50',
                )}>
                  <input type="checkbox" checked={on} onChange={() => toggle(it.id)} className="w-4 h-4 accent-brand-500" />
                  <span className="break-words" title={`${it.name} / ${it.uf}`}>
                    {it.name}{mode === 'city' && it.uf ? <span className="text-gray-400"> / {it.uf}</span> : null}
                  </span>
                </label>
              )
            })}
          </div>
        )}

        <div className="mt-5 flex justify-end">
          <Button onClick={generate} loading={generating} iconLeft={<FileDown size={14} />}>
            Gerar PDF
          </Button>
        </div>
      </div>
    </>
  )
}

function ModeButton({ active, onClick, icon, divider, children }: {
  active: boolean; onClick: () => void; icon: React.ReactNode; divider?: boolean; children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'cursor-pointer flex items-center gap-2 px-4 py-2 text-sm font-medium transition',
        divider && 'border-l border-gray-200',
        active ? 'bg-brand-500 text-white hover:bg-brand-600' : 'text-gray-600 hover:bg-gray-50',
      )}
    >
      {icon}{children}
    </button>
  )
}

export default MyCompanyCatalogPage
